import { z } from "zod";
import { AdapterKindSchema, TenantKeyFieldSchema } from "./adapter";
import {
	ExternalWorkspaceRefSchema,
	WorkspaceExternalRefSchema,
	WorkspaceIdSchema,
	WorkspaceNameSchema,
} from "./workspace";

/**
 * Inputs for the `/organization` client, the Organisation administration
 * surface, as distinct from `/instance`, where HTCTW administers the ELASO
 * Instance itself.
 *
 * EVERY procedure here names the Organisation it acts on (TECH-3344,
 * docs/adr/32-the-root-organisation-and-arranged-access.md). That REVERSES
 * TECH-3199, where no schema in this file carried an `organizationId` and the
 * gate read the tenant from the caller's own `User` row. The reversal is not a
 * relaxation, it is the price of what record 32 asks for: a Root Organisation
 * owner or admin manages the structure of every Organisation on the Instance,
 * from this surface, so there is no longer one tenant to infer from who is
 * asking, and it has to be said out loud.
 *
 * What replaces what was lost. Cross-tenant access used to be IMPOSSIBLE here,
 * because no field could carry another tenant's id, and the old comment in this
 * place said so at length. It is now CHECKED, in exactly one place:
 * `authorizeOrgAdmin` in `lib/authz/gate.ts` admits a caller on the named
 * Organisation only when it is the Organisation on their own `User` row, or
 * when they hold Root Organisation authority, and refuses every other pairing
 * with one indistinct message. An id on the wire is a claim, never a fact.
 * Nothing downstream of the gate re-decides it, and nothing here defaults it.
 *
 * `OrganizationIdSchema` is the same shape `/instance` requires (TECH-3206), and
 * it is DECLARED here rather than there because `platform.ts` already imports
 * this module. Declaring it there and importing it back would close a module
 * cycle, and both modules build schemas at module scope, so the loser of the
 * cycle would evaluate `z.object({...})` against an undefined field.
 *
 * The workspace procedures also carry a `workspaceId`, and it is checked the
 * same way it always was. `assertWorkspaceInOrganization` turns the caller's
 * claim into a fact against the Organisation the gate cleared, before anything
 * is written, and refuses as NotFound so a probe cannot learn that another
 * tenant's workspace exists.
 *
 * Targets are addressed by email. Grant and revoke name their target by email
 * rather than by uid, because an email is what an administrator has in front of
 * them, and resolving it against the named Organisation is what stops authority
 * being handed to a stranger. Emails are trimmed and lower-cased here because
 * that is the form the import writes (`User.email`), and the lookup is an
 * exact-match unique read.
 */

// Trim and lower-case BEFORE validating, so the address an administrator typed
// is judged, and looked up, in the normalized form the import writes. `.pipe()`
// preserves that order in Zod 4, because `z.email()` is a schema in its own
// right rather than a refinement on `z.string()`, so the normalizing string
// schema runs first and hands its output on to be validated.
//
// Exported because the Root Organisation's authority procedures address their
// target the same way, and the normalization is the half that MATTERS. It
// decides which stored row an address finds. Two copies of it would be two
// answers to "is `Ada@Example.com ` this user", which is not a thing to have
// twice.
export const OrganizationMemberEmailSchema = z
	.string()
	.trim()
	.toLowerCase()
	.pipe(z.email());

/**
 * The Organisation a procedure acts on. ObjectId shape, because `Organization`
 * predates the ULID rule. Shared with `/instance` (TECH-3206), which requires
 * one wherever it names an Organisation, for the same reason this
 * surface now does: the server never defaults a tenant.
 *
 * A payload whose `organizationId` fails this shape never reaches the parser.
 * The gate reads the field first and refuses a malformed one exactly as it
 * refuses a well-formed one the caller may not administer, so the two are
 * indistinguishable from outside.
 */
export const OrganizationIdSchema = z.string().regex(/^[0-9a-f]{24}$/);

/**
 * `organization.me`, the ungated access probe. Takes nothing, and still answers
 * only about the caller's OWN Organisation, the one their `User` row names. It
 * is no longer the whole of what the surface needs to know, because a Root
 * Organisation admin may have no `User` row in the Organisation they are about
 * to administer, and may have none at all. `organization.open` answers about
 * the Organisation the caller names.
 */
export const OrganizationMeInputSchema = z.object({}).optional();
export type OrganizationMeInput = z.infer<typeof OrganizationMeInputSchema>;

/**
 * `organization.open` opens a window of the Organisation Admin Client on the
 * one Organisation it names, which is the address the window was opened at
 * (docs/adr/39-two-admin-clients-the-elaso-instance-admin-client-and-the-organisation-admin-client.md).
 */
export const OpenOrganizationInputSchema = z.object({
	organizationId: OrganizationIdSchema,
});
export type OpenOrganizationInput = z.infer<typeof OpenOrganizationInputSchema>;

/** `organization.listWorkspaces` returns the named Organisation's Workspaces. */
export const ListOrganizationWorkspacesInputSchema = z.object({
	organizationId: OrganizationIdSchema,
});
export type ListOrganizationWorkspacesInput = z.infer<
	typeof ListOrganizationWorkspacesInputSchema
>;

/** `organization.listAdmins` returns who holds authority there, labelled by tier. */
export const ListOrganizationAdminsInputSchema = z.object({
	organizationId: OrganizationIdSchema,
});
export type ListOrganizationAdminsInput = z.infer<
	typeof ListOrganizationAdminsInputSchema
>;

const MAX_DISPLAY_NAME_LENGTH = 100;

/**
 * The name a grant gives a member it creates the record for. Required by the
 * server only then: a member who already has a record keeps the name it holds.
 */
/**
 * The refusal of a grant that would create an account with no name to give it,
 * an administrator's or a Program Director's. Shared so the client can show it
 * under the name field it is about.
 */
export const NEW_ACCOUNT_NEEDS_A_NAME =
	"This address has no account yet: add their name";

export const AdministratorNameSchema = z
	.string()
	.trim()
	.min(1)
	.max(MAX_DISPLAY_NAME_LENGTH);

/**
 * `organization.grantAdmin` adds an administrator by email, creating the
 * member's record and account when the address has none, which needs their
 * name.
 */
export const GrantOrganizationAdminInputSchema = z.object({
	organizationId: OrganizationIdSchema,
	email: OrganizationMemberEmailSchema,
	displayName: AdministratorNameSchema.optional(),
});
export type GrantOrganizationAdminInput = z.infer<
	typeof GrantOrganizationAdminInputSchema
>;

/** `organization.revokeAdmin` removes an administrator. Never yourself. */
export const RevokeOrganizationAdminInputSchema = z.object({
	organizationId: OrganizationIdSchema,
	email: OrganizationMemberEmailSchema,
});
export type RevokeOrganizationAdminInput = z.infer<
	typeof RevokeOrganizationAdminInputSchema
>;

/** `organization.grantOwner` promotes an existing admin to owner. */
export const GrantOrganizationOwnerInputSchema = z.object({
	organizationId: OrganizationIdSchema,
	email: OrganizationMemberEmailSchema,
});
export type GrantOrganizationOwnerInput = z.infer<
	typeof GrantOrganizationOwnerInputSchema
>;

/**
 * `organization.revokeOwner` takes an owner's tier away, the caller's own
 * included, which is stepping down. They stay an admin, and the last owner is
 * always kept.
 */
export const RevokeOrganizationOwnerInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	email: OrganizationMemberEmailSchema,
});
export type RevokeOrganizationOwnerInput = z.infer<
	typeof RevokeOrganizationOwnerInputSchema
>;

/**
 * `organization.resendWelcome` sends the welcome email again to an owner or
 * admin still waiting to set their first password. By uid, as the row carries
 * it: the server finds the account and checks the uid holds a tier here.
 */
export const ResendAdministratorWelcomeInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	firebaseUid: z.string().trim().min(1),
});
export type ResendAdministratorWelcomeInput = z.infer<
	typeof ResendAdministratorWelcomeInputSchema
>;

/** `organization.listProgramDirectors`: every workspace's, in one answer. */
export const ListProgramDirectorsInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
});
export type ListProgramDirectorsInput = z.infer<
	typeof ListProgramDirectorsInputSchema
>;

/**
 * `organization.grantProgramDirector` gives one of the Organisation's
 * workspaces a Program Director by email, creating the member's record and
 * account when the address has none, which needs their name.
 */
export const GrantProgramDirectorInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	workspaceId: WorkspaceIdSchema,
	email: OrganizationMemberEmailSchema,
	displayName: AdministratorNameSchema.optional(),
});
export type GrantProgramDirectorInput = z.infer<
	typeof GrantProgramDirectorInputSchema
>;

/**
 * `organization.resendProgramDirectorWelcome` sends the welcome email again to a
 * Program Director of the workspace still waiting to set their first password.
 */
export const ResendProgramDirectorWelcomeInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	workspaceId: WorkspaceIdSchema,
	email: OrganizationMemberEmailSchema,
});
export type ResendProgramDirectorWelcomeInput = z.infer<
	typeof ResendProgramDirectorWelcomeInputSchema
>;

/**
 * `organization.revokeProgramDirector` takes the role away; someone it leaves
 * with no role in the workspace leaves the workspace too.
 */
export const RevokeProgramDirectorInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	workspaceId: WorkspaceIdSchema,
	email: OrganizationMemberEmailSchema,
});
export type RevokeProgramDirectorInput = z.infer<
	typeof RevokeProgramDirectorInputSchema
>;

/**
 * `organization.createWorkspace` creates a workspace with nothing upstream
 * behind it. One made from an ExternalWorkspace, a course or program in the
 * Organisation's own system, is `organization.createWorkspaceFromExternalWorkspace`.
 */
export const CreateOrganizationWorkspaceInputSchema = z.object({
	organizationId: OrganizationIdSchema,
	name: WorkspaceNameSchema,
});
export type CreateOrganizationWorkspaceInput = z.infer<
	typeof CreateOrganizationWorkspaceInputSchema
>;

/** `organization.renameWorkspace` renames a workspace in the caller's own Organization. */
export const RenameOrganizationWorkspaceInputSchema = z.object({
	organizationId: OrganizationIdSchema,
	workspaceId: WorkspaceIdSchema,
	name: WorkspaceNameSchema,
});
export type RenameOrganizationWorkspaceInput = z.infer<
	typeof RenameOrganizationWorkspaceInputSchema
>;

/**
 * `organization.setWorkspaceExternalRef` binds one of the caller's workspaces
 * to a course or program in their own external system, or clears the binding
 * (TECH-3244).
 *
 * The reference namespace is the Organization's, and the Organization is the
 * gate's, so a caller can only name something within their own upstream. That
 * is why this belongs on this client at all.
 */
export const SetOrganizationWorkspaceExternalRefInputSchema = z.object({
	organizationId: OrganizationIdSchema,
	workspaceId: WorkspaceIdSchema,
	externalRef: WorkspaceExternalRefSchema,
});
export type SetOrganizationWorkspaceExternalRefInput = z.infer<
	typeof SetOrganizationWorkspaceExternalRefInputSchema
>;

/** `organization.setWorkspaceEnabled` switches one of the caller's workspaces off or on. */
export const SetOrganizationWorkspaceEnabledInputSchema = z.object({
	organizationId: OrganizationIdSchema,
	workspaceId: WorkspaceIdSchema,
	enabled: z.boolean(),
});
export type SetOrganizationWorkspaceEnabledInput = z.infer<
	typeof SetOrganizationWorkspaceEnabledInputSchema
>;

/** `organization.listExternalWorkspaces` lists the Organisation's ExternalWorkspaces. */
export const ListOrganizationExternalWorkspacesInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
});
export type ListOrganizationExternalWorkspacesInput = z.infer<
	typeof ListOrganizationExternalWorkspacesInputSchema
>;

/**
 * `organization.previewExternalWorkspaceRoster` shows who an ExternalWorkspace's import would bring
 * in, and what it would change, recording what it showed
 * (docs/adr/39-two-admin-clients-the-elaso-instance-admin-client-and-the-organisation-admin-client.md).
 */
export const PreviewExternalWorkspaceRosterInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	externalRef: ExternalWorkspaceRefSchema,
});
export type PreviewExternalWorkspaceRosterInput = z.infer<
	typeof PreviewExternalWorkspaceRosterInputSchema
>;

/**
 * `organization.createWorkspaceFromExternalWorkspace` makes the workspace for an
 * ExternalWorkspace, or finishes the one an earlier attempt left part-made.
 */
export const CreateWorkspaceFromExternalWorkspaceInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	externalRef: ExternalWorkspaceRefSchema,
	name: WorkspaceNameSchema,
});
export type CreateWorkspaceFromExternalWorkspaceInput = z.infer<
	typeof CreateWorkspaceFromExternalWorkspaceInputSchema
>;

/**
 * `organization.importExternalWorkspaceRoster` brings a linked workspace's upstream roster
 * in, users and roles together.
 */
export const ImportExternalWorkspaceRosterInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	workspaceId: WorkspaceIdSchema,
});
export type ImportExternalWorkspaceRosterInput = z.infer<
	typeof ImportExternalWorkspaceRosterInputSchema
>;

/**
 * The reason an arranged-access grant is written with
 * (docs/adr/32-the-root-organisation-and-arranged-access.md, TECH-3349).
 *
 * TRIMMED, THEN LENGTH-CHECKED, so that a reason of spaces is the same refusal
 * as a reason of nothing. Record 32 refuses a grant with no reason and makes no
 * change; " " satisfies a bare `min(1)` and says as little, so the transform
 * runs first and the stored reason is what a reader would quote.
 *
 * Capped because the reason is an identifier's neighbour in a record that holds
 * identifiers and never data: a support note belongs in the ticket it names, and
 * the cap is what keeps somebody pasting a learner's message in here.
 */
/**
 * Long enough for a ticket reference and a sentence saying what is wrong, short
 * enough that the field is not a place to paste a conversation into. The reason
 * is read by a person reviewing the Administrative Audit Log, not parsed.
 */
const REASON_MAX_LENGTH = 500;

export const ArrangedAccessReasonSchema = z
	.string()
	.transform((value) => value.trim())
	.refine((value) => value.length > 0, {
		message: "A reason is required to arrange access",
	})
	.refine((value) => value.length <= REASON_MAX_LENGTH, {
		message: `A reason cannot be longer than ${REASON_MAX_LENGTH} characters`,
	});

/**
 * `organization.arrangeAccess` — the caller writes themself a direct grant into
 * one of the named Organisation's workspaces, with a stated reason (record 32).
 *
 * No `firebaseUid` and no `email`. The grant is always for the caller, and a
 * field naming somebody else would be a field the server has to refuse on every
 * call.
 *
 * STRICT, so naming one is an ERROR rather than a field quietly dropped. A
 * plain `z.object` strips what it does not know, so a client that sent
 * `{ email: "colleague@school.test" }` would be told the grant succeeded and
 * would have arranged access for the CALLER — the one misunderstanding on this
 * surface that a success toast must never cover. The two below are strict for
 * the same reason, and it matches the root-authority inputs on `/instance`.
 */
export const ArrangeAccessInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	workspaceId: WorkspaceIdSchema,
	reason: ArrangedAccessReasonSchema,
	/** Omitted takes the default the route names; a role in this workspace. */
	roleId: z.string().min(1).optional(),
});
export type ArrangeAccessInput = z.infer<typeof ArrangeAccessInputSchema>;

/** `organization.listMyArrangedAccess` — the caller's own standing grants. */
export const ListMyArrangedAccessInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
});
export type ListMyArrangedAccessInput = z.infer<
	typeof ListMyArrangedAccessInputSchema
>;

/** `organization.revokeArrangedAccess` — the caller hands one back. */
export const RevokeArrangedAccessInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	workspaceId: WorkspaceIdSchema,
});
export type RevokeArrangedAccessInput = z.infer<
	typeof RevokeArrangedAccessInputSchema
>;

/**
 * `organization.listArrangedAccess` — the Organisation's own administrators read
 * every arranged grant standing in its workspaces.
 */
export const ListArrangedAccessInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
});
export type ListArrangedAccessInput = z.infer<
	typeof ListArrangedAccessInputSchema
>;

/**
 * `organization.removeArrangedAccess` — the Organisation's own administrator ends
 * one arranged grant. `userId` is the User row the grant's origin is kept on, as
 * `listArrangedAccess` returns it; ObjectId shape, like the Organisation's.
 * `instanceAdmin.endArrangedAccess` takes the same, from the Instance-wide list.
 */
export const RemoveArrangedAccessInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	workspaceId: WorkspaceIdSchema,
	userId: z.string().regex(/^[0-9a-f]{24}$/),
});
export type RemoveArrangedAccessInput = z.infer<
	typeof RemoveArrangedAccessInputSchema
>;

const MAX_ORGANIZATION_NAME_LENGTH = 100;

/**
 * An Organisation's name, as it is created and as it is renamed: trimmed, and 1
 * to 100 characters. Unique among the Organisations under the same parent, which
 * the server checks, because only it can see the siblings.
 */
export const OrganizationNameSchema = z
	.string()
	.trim()
	.min(1)
	.max(MAX_ORGANIZATION_NAME_LENGTH);

/**
 * `organization.rename` renames the Organisation, and nothing else about it: the
 * login hostname of an identity home is fixed at creation
 * (docs/adr/33-identity-homes-sign-in-chosen-by-the-address.md). For the
 * Organisation's owners and for Instance Manager owners and admins.
 */
export const RenameOrganizationInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	name: OrganizationNameSchema,
});
export type RenameOrganizationInput = z.infer<
	typeof RenameOrganizationInputSchema
>;

/**
 * `organization.readAdapter` reads the Organisation's adapter registration and
 * the form each adapter kind is edited with. For Instance Manager owners and
 * admins.
 */
export const ReadOrganizationAdapterInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
});
export type ReadOrganizationAdapterInput = z.infer<
	typeof ReadOrganizationAdapterInputSchema
>;

/**
 * `organization.saveAdapter` writes the Organisation's adapter registration,
 * adding one to an Organisation that has none
 * (docs/adr/26-adapters-the-only-entry-point-for-outside-data.md).
 *
 * The shape mirrors the stored row, not any one adapter's vocabulary: the two
 * columns a launch routes on, and the `config` object, which the server checks
 * against the adapter's own schema before writing. `kind` is chosen when the
 * adapter is added and never changed by this procedure; replacing one adapter
 * with another discards the registration, which is a different act.
 */
export const SaveOrganizationAdapterInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	kind: AdapterKindSchema,
	externalIssuer: TenantKeyFieldSchema,
	externalClientId: TenantKeyFieldSchema,
	config: z.record(z.string(), z.unknown()),
});
export type SaveOrganizationAdapterInput = z.infer<
	typeof SaveOrganizationAdapterInputSchema
>;

/**
 * `organization.setEnabled` switches the Organisation off or back on. For
 * Instance Manager owners and admins, who alone can reach it while it is off.
 */
export const SetOrganizationSwitchInputSchema = z.strictObject({
	organizationId: OrganizationIdSchema,
	enabled: z.boolean(),
});
export type SetOrganizationSwitchInput = z.infer<
	typeof SetOrganizationSwitchInputSchema
>;
